import {ref} from 'vue'
import {BASE_URL} from "@/composables/url_config"
import {log} from "@/composables/useLog"
import axios from "axios"

const error = ref(null)

const getHeaders = (token) => ({
    "Authorization": `Bearer ${token}`,
    "Content-Type": "application/json",
    "Accept": "application/json"
})

// ================== TRAFFIC ALERT RULES ==================

export const getTrafficAlertRules = async (token, filters = {}) => {
    log("getTrafficAlertRules called")
    if (!token) return {error: true, message: "No token provided", rules: []}
    try {
        const result = await axios.get(BASE_URL + '/traffic_alerts/get_rules_api', {headers: getHeaders(token), params: filters})
        return result.data
    } catch (err) {
        log("Error in getTrafficAlertRules: " + err.message)
        error.value = err.message
        return {error: true, message: "Error loading traffic alert rules", rules: []}
    }
}

export const createTrafficAlertRule = async (token, ruleData) => {
    log("createTrafficAlertRule called")
    if (!token) return {error: true, message: "No token provided"}
    try {
        const result = await axios.post(BASE_URL + '/traffic_alerts/create_rule_api', ruleData, {headers: getHeaders(token)})
        return result.data
    } catch (err) {
        log("Error in createTrafficAlertRule: " + err.message)
        error.value = err.message
        return {error: true, message: "Error creating traffic alert rule"}
    }
}

export const updateTrafficAlertRule = async (token, ruleData) => {
    log("updateTrafficAlertRule called")
    if (!token) return {error: true, message: "No token provided"}
    try {
        const result = await axios.put(BASE_URL + '/traffic_alerts/update_rule_api', ruleData, {headers: getHeaders(token)})
        return result.data
    } catch (err) {
        log("Error in updateTrafficAlertRule: " + err.message)
        error.value = err.message
        return {error: true, message: "Error updating traffic alert rule"}
    }
}

export const deleteTrafficAlertRule = async (token, ruleId) => {
    log("deleteTrafficAlertRule called")
    if (!token) return {error: true, message: "No token provided"}
    try {
        const result = await axios.delete(BASE_URL + '/traffic_alerts/delete_rule_api', {headers: getHeaders(token), params: {id: ruleId}})
        return result.data
    } catch (err) {
        log("Error in deleteTrafficAlertRule: " + err.message)
        error.value = err.message
        return {error: true, message: "Error deleting traffic alert rule"}
    }
}

export const toggleTrafficAlertRule = async (token, ruleId) => {
    log("toggleTrafficAlertRule called")
    if (!token) return {error: true, message: "No token provided"}
    try {
        const result = await axios.put(BASE_URL + '/traffic_alerts/toggle_rule_api', {id: ruleId}, {headers: getHeaders(token)})
        return result.data
    } catch (err) {
        log("Error in toggleTrafficAlertRule: " + err.message)
        error.value = err.message
        return {error: true, message: "Error toggling traffic alert rule"}
    }
}

// ================== LIMITATION PROFILES ==================

export const getLimitationProfiles = async (token, filters = {}) => {
    log("getLimitationProfiles called")
    if (!token) return {error: true, message: "No token provided", profiles: []}
    try {
        const result = await axios.get(BASE_URL + '/traffic_alerts/get_profiles_api', {headers: getHeaders(token), params: filters})
        return result.data
    } catch (err) {
        log("Error in getLimitationProfiles: " + err.message)
        error.value = err.message
        return {error: true, message: "Error loading limitation profiles", profiles: []}
    }
}

export const createLimitationProfile = async (token, profileData) => {
    log("createLimitationProfile called")
    if (!token) return {error: true, message: "No token provided"}
    try {
        const result = await axios.post(BASE_URL + '/traffic_alerts/create_profile_api', profileData, {headers: getHeaders(token)})
        return result.data
    } catch (err) {
        log("Error in createLimitationProfile: " + err.message)
        error.value = err.message
        return {error: true, message: "Error creating limitation profile"}
    }
}

export const updateLimitationProfile = async (token, profileData) => {
    log("updateLimitationProfile called")
    if (!token) return {error: true, message: "No token provided"}
    try {
        const result = await axios.put(BASE_URL + '/traffic_alerts/update_profile_api', profileData, {headers: getHeaders(token)})
        return result.data
    } catch (err) {
        log("Error in updateLimitationProfile: " + err.message)
        error.value = err.message
        return {error: true, message: "Error updating limitation profile"}
    }
}

export const deleteLimitationProfile = async (token, profileId) => {
    log("deleteLimitationProfile called")
    if (!token) return {error: true, message: "No token provided"}
    try {
        const result = await axios.delete(BASE_URL + '/traffic_alerts/delete_profile_api', {headers: getHeaders(token), params: {id: profileId}})
        return result.data
    } catch (err) {
        log("Error in deleteLimitationProfile: " + err.message)
        error.value = err.message
        return {error: true, message: "Error deleting limitation profile"}
    }
}

export const toggleLimitationProfile = async (token, profileId) => {
    log("toggleLimitationProfile called")
    if (!token) return {error: true, message: "No token provided"}
    try {
        const result = await axios.put(BASE_URL + '/traffic_alerts/toggle_profile_api', {id: profileId}, {headers: getHeaders(token)})
        return result.data
    } catch (err) {
        log("Error in toggleLimitationProfile: " + err.message)
        error.value = err.message
        return {error: true, message: "Error toggling limitation profile"}
    }
}

// ================== PROFILE ASSIGNMENTS ==================

export const getProfileAssignments = async (token, profileId) => {
    log("getProfileAssignments called")
    if (!token) return {error: true, message: "No token provided", assignments: []}
    try {
        const result = await axios.get(BASE_URL + '/traffic_alerts/get_assignments_api', {headers: getHeaders(token), params: {limitation_profile_id: profileId}})
        return result.data
    } catch (err) {
        log("Error in getProfileAssignments: " + err.message)
        error.value = err.message
        return {error: true, message: "Error loading assignments", assignments: []}
    }
}

export const createProfileAssignment = async (token, assignmentData) => {
    log("createProfileAssignment called")
    if (!token) return {error: true, message: "No token provided"}
    try {
        const result = await axios.post(BASE_URL + '/traffic_alerts/create_assignment_api', assignmentData, {headers: getHeaders(token)})
        return result.data
    } catch (err) {
        log("Error in createProfileAssignment: " + err.message)
        error.value = err.message
        return {error: true, message: "Error creating assignment"}
    }
}

export const deleteProfileAssignment = async (token, assignmentId) => {
    log("deleteProfileAssignment called")
    if (!token) return {error: true, message: "No token provided"}
    try {
        const result = await axios.delete(BASE_URL + '/traffic_alerts/delete_assignment_api', {headers: getHeaders(token), params: {id: assignmentId}})
        return result.data
    } catch (err) {
        log("Error in deleteProfileAssignment: " + err.message)
        error.value = err.message
        return {error: true, message: "Error deleting assignment"}
    }
}

// ================== LOGS ==================

export const getTrafficAlertLogs = async (token, filters = {}) => {
    log("getTrafficAlertLogs called")
    if (!token) return {error: true, message: "No token provided", logs: []}
    try {
        const result = await axios.get(BASE_URL + '/traffic_alerts/get_logs_api', {headers: getHeaders(token), params: filters})
        return result.data
    } catch (err) {
        log("Error in getTrafficAlertLogs: " + err.message)
        error.value = err.message
        return {error: true, message: "Error loading traffic alert logs", logs: []}
    }
}

// ================== PROCESS ALERTS ==================

export const processTrafficAlerts = async (token, mode = 'sync') => {
    log("processTrafficAlerts called, mode=" + mode)
    if (!token) return {error: true, message: "No token provided"}
    try {
        const result = await axios.post(BASE_URL + '/traffic_alerts/process_alerts_api', {mode}, {headers: getHeaders(token)})
        return result.data
    } catch (err) {
        log("Error in processTrafficAlerts: " + err.message)
        error.value = err.message
        return {error: true, message: "Error processing traffic alerts"}
    }
}

// ================== DRY RUN ==================

export const trafficAlertsDryRun = async (token) => {
    log("trafficAlertsDryRun called")
    if (!token) return {error: true, message: "No token provided", results: []}
    try {
        const result = await axios.post(BASE_URL + '/traffic_alerts/dry_run_api', {}, {headers: getHeaders(token)})
        return result.data
    } catch (err) {
        log("Error in trafficAlertsDryRun: " + err.message)
        error.value = err.message
        return {error: true, message: "Error running dry run", results: []}
    }
}

// ================== CLIENT IMSI GROUPS & SIMS ==================

export const getClientImsiGroups = async (token, clientId) => {
    log("getClientImsiGroups called for client " + clientId)
    if (!token) return {error: true, message: "No token provided", imsi_groups: []}
    try {
        const result = await axios.get(BASE_URL + '/traffic_alerts/get_client_imsi_groups_api', {headers: getHeaders(token), params: {client_id: clientId}})
        return result.data
    } catch (err) {
        log("Error in getClientImsiGroups: " + err.message)
        error.value = err.message
        return {error: true, message: "Error loading IMSI groups", imsi_groups: []}
    }
}

export const getClientSims = async (token, clientId) => {
    log("getClientSims called for client " + clientId)
    if (!token) return {error: true, message: "No token provided", sims: []}
    try {
        const result = await axios.get(BASE_URL + '/traffic_alerts/get_client_sims_api', {headers: getHeaders(token), params: {client_id: clientId}})
        return result.data
    } catch (err) {
        log("Error in getClientSims: " + err.message)
        error.value = err.message
        return {error: true, message: "Error loading SIMs", sims: []}
    }
}

// ================== SIM LIMITATION PROFILE ==================

export const getSimLimitationProfile = async (token, simId) => {
    log("getSimLimitationProfile called for sim " + simId)
    if (!token) return {error: true, message: "No token provided", profile: null}
    try {
        const result = await axios.get(BASE_URL + '/traffic_alerts/get_sim_limitation_profile_api', {headers: getHeaders(token), params: {sim_id: simId}})
        return result.data
    } catch (err) {
        log("Error in getSimLimitationProfile: " + err.message)
        error.value = err.message
        return {error: true, message: "Error loading SIM limitation profile", profile: null}
    }
}

export const assignSimProfile = async (token, simId, profileId) => {
    log("assignSimProfile called for sim " + simId + " profile " + profileId)
    if (!token) return {error: true, message: "No token provided"}
    try {
        const result = await axios.post(BASE_URL + '/traffic_alerts/assign_sim_profile_api', {sim_id: simId, limitation_profile_id: profileId}, {headers: getHeaders(token)})
        return result.data
    } catch (err) {
        log("Error in assignSimProfile: " + err.message)
        error.value = err.message
        return {error: true, message: "Error assigning profile to SIM"}
    }
}

export const removeSimProfile = async (token, simId) => {
    log("removeSimProfile called for sim " + simId)
    if (!token) return {error: true, message: "No token provided"}
    try {
        const result = await axios.delete(BASE_URL + '/traffic_alerts/remove_sim_profile_api', {headers: getHeaders(token), params: {sim_id: simId}})
        return result.data
    } catch (err) {
        log("Error in removeSimProfile: " + err.message)
        error.value = err.message
        return {error: true, message: "Error removing profile from SIM"}
    }
}

// ================== ENUMS ==================

export const getTrafficAlertEnums = async (token) => {
    log("getTrafficAlertEnums called")
    if (!token) return {error: true, message: "No token provided"}
    try {
        const result = await axios.get(BASE_URL + '/traffic_alerts/get_enums_api', {headers: getHeaders(token)})
        return result.data
    } catch (err) {
        log("Error in getTrafficAlertEnums: " + err.message)
        error.value = err.message
        return {error: true, message: "Error loading enums"}
    }
}
